import { Fragment } from "react/jsx-runtime";
import classNames from "classnames/bind";
import { ProductCard } from "../ProductCard";
import { isInStock } from "../utils/isInStock";
import { ConfigProps, Product, ProductListLayout } from "../../types/types";
import styles from "./Grid.module.scss";
import {
  CreditBanner,
  CreditBannerType,
} from "../../product-list-components/CreditBanner/CreditBanner";
import { YouveViewed } from "../../product-list-components/YouveViewed/YouveViewed";
import { useFeatureFlags } from "@~/flags/flags";
import { DisruptorBlock } from "@~/product-list-components/DisruptorBlock/DisruptorBlock";

const cx = classNames.bind(styles);

const getContainerTestId = (
  configProps: ConfigProps,
  largeCardsEnabled: boolean,
) => {
  if (configProps?.fullWidth) {
    return "grid-container-fullWidth";
  }
  if (configProps?.listView) {
    return "grid-container-list-view";
  }
  if (largeCardsEnabled) {
    return "grid-container-large-cards";
  }
  return "grid-container-small-cards";
};

export const Grid: ProductListLayout = ({
  configProps,
  config,
  products,
  resultsInfo,
}) => {
  const flags = useFeatureFlags();
  const youveViewedEnabled = configProps?.youveViewed ?? true;
  const creditBannersEnabled = configProps?.creditBanner ?? true;
  const disruptorBlocksEnabled = configProps?.disruptorBlock ?? true;
  const hideOutOfStocks = config["product-list-layout"]?.props?.hideOutOfStocks;
  const largeCardsEnabled = Boolean(flags.largeCards?.value);

  const filteredProducts = products.filter((product) =>
    hideOutOfStocks ? isInStock(product) : true,
  );

  const creditBannerDetails = getCreditBannerDetails(
    filteredProducts,
    resultsInfo.chunkSize,
  );

  return (
    <section
      className={cx("grid-container", {
        "grid-container--fullWidth":
          largeCardsEnabled && configProps?.fullWidth,
        "grid-container--large-cards":
          largeCardsEnabled && !configProps?.fullWidth,
        "grid-container--list-view": configProps?.listView,
        "grid-container--curated-categories": configProps?.curatedCategories,
      })}
      data-testid={getContainerTestId(configProps, largeCardsEnabled)}
    >
      {filteredProducts.map((product, ind) => {
        const hasCreditBanner =
          creditBannersEnabled && creditBannerDetails.indices.includes(ind);

        return (
          <Fragment
            key={
              product.advertising
                ? `${product.aliases.pimProductId}-sponsored`
                : product.aliases.pimProductId
            }
          >
            <ProductCard product={product} config={config} index={ind} />

            {disruptorBlocksEnabled && (
              <DisruptorBlock
                flags={flags}
                indexInGrid={ind}
                productCount={filteredProducts.length}
                finalCreditBannerIndex={
                  creditBannerDetails.indices[
                    creditBannerDetails.indices.length - 1
                  ] || 0
                }
              />
            )}

            {hasCreditBanner && (
              <CreditBanner
                creditBannerType={creditBannerDetails.type}
                className={styles.fullWidth}
              />
            )}

            {youveViewedEnabled && (
              <YouveViewed
                resultsInfo={resultsInfo}
                numProductsOnPageAbove={ind + 1}
              />
            )}
          </Fragment>
        );
      })}
    </section>
  );
};

type CreditBannerDetails = {
  type: CreditBannerType;
  indices: number[];
};

const getCreditBannerDetails = (
  products: Product[],
  chunkSize: number,
): CreditBannerDetails => {
  const firstIfcIndex = products.findIndex(
    (p) => p.credit?.illustration?.financialProductType === "IFC",
  );
  const firstIbcIndex = products.findIndex(
    (p) => p.credit?.illustration?.financialProductType === "IBC",
  );

  if (firstIfcIndex === -1 && firstIbcIndex === -1) {
    return { type: null, indices: [] };
  }
  if (firstIbcIndex === -1) {
    return { type: "IFC", indices: [firstIfcIndex] };
  }
  if (firstIfcIndex === -1) {
    return { type: "IBC", indices: [firstIbcIndex] };
  }
  if (
    Math.floor(firstIfcIndex / chunkSize) ===
    Math.floor(firstIbcIndex / chunkSize)
  ) {
    return {
      type: "IFC-IBC",
      indices: [Math.min(firstIfcIndex, firstIbcIndex)],
    };
  }
  return { type: "IFC-IBC", indices: [firstIbcIndex, firstIfcIndex].sort() };
};
